import React, { useRef, createContext, useContext, useCallback, useState, useEffect } from "react";

export default function createFastContext<Store>(initialState: Store) {
  function useStoreData(): {
    get: () => Store;
    set: (value: Partial<Store>) => void;
    subscribe: (callback: () => void) => () => void;
  } {
    const store = useRef(initialState);

    const get = useCallback(() => store.current, []);

    const subscribers = useRef(new Set<() => void>());

    const set = useCallback((value: Partial<Store>) => {
      store.current = { ...store.current, ...value };
      subscribers.current.forEach((callback) => callback());
    }, []);

    const subscribe = useCallback((callback: () => void) => {
      subscribers.current.add(callback);
      return () => subscribers.current.delete(callback);
    }, []);

    return {
      get,
      set,
      subscribe,
    };
  }

  type UseStoreDataReturnType = ReturnType<typeof useStoreData>;

  const StoreContext = createContext<UseStoreDataReturnType | null>(null);

  type ProviderProps = {
    children: React.ReactNode;
    storeValue?: Store;
  };
  function Provider(props: ProviderProps) {
    const { children, storeValue } = props;
    const store = useStoreData();

    // Update the store with the storeValue if provided
    useEffect(() => {
      if (store && storeValue && store.get() !== storeValue) {
        store.set(storeValue);
      }
    }, [store]);

    return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
  }

  function useStore(): [Store, (value: Partial<Store>) => void] {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error("Store not found");
    }

    const [state, setState] = useState(store.get());

    useEffect(() => {
      const unsubscribe = store.subscribe(() => setState(store.get()));
      return () => {
        unsubscribe();
      };
    }, []);

    return [state, store.set];
  }

  // Support another useStore that won't throw an error if the context is not found
  function useStoreOptional(): [Store | null, ((value: Partial<Store>) => void) | null] {
    const store = useContext(StoreContext);
    const [state, setState] = useState(store?.get());
    useEffect(() => {
      const unsubscribe = store?.subscribe(() => setState(store?.get()));
      return () => {
        unsubscribe?.();
      };
    }, []);

    return [state || null, store?.set || null];
  }

  return {
    Provider,
    useStore,
    useStoreOptional,
  };
}
